import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { formatCents } from './_money';
import { saveCsv } from './_download';

/**
 * src/pages/reports/AffiliatePanel.js — the affiliate programme on the CEO
 * dashboard. Card P204 PR 2.
 *
 * Presentational. It renders exactly what reports:affiliate:summary returned
 * and computes no figure of its own — every number on screen traces to a
 * server query. The only arithmetic here is SORTING and CSV formatting, and
 * both are exported as pure functions because that is what can be tested.
 *
 * ─── ⛔⛔ NOTHING ABOUT COMMISSIONS BUT ONE SENTENCE ────────────────────────
 * Daniel's call, 31 Oct. No tile, no column, no CSV header, no tooltip. The
 * panel renders `payload.commissions.note` and nothing else, and a test
 * red-arms the absence across the payload AND the CSV.
 *
 * ─── ⚠ TIER IS LABELLED WITH ITS DATE, ALWAYS ──────────────────────────────
 * Tier recalc kept running nightly until 2026-10-03 while the money path was
 * already off, so a tier can be ahead of anything the ledger priced. The
 * column header carries the date; the field is named `tierAsOf` server-side so
 * a template cannot render it as "tier" by accident.
 *
 * ─── ⚠ WHY THE LOGIC IS NOT IN THE CHART ───────────────────────────────────
 * recharts does not lay out in jsdom — no tick labels, no sized bars — so an
 * assertion against the SVG passes or fails for reasons unrelated to the data.
 * The ordering and the CSV are therefore pure functions, tested directly.
 */

/** Columns the table can sort by. Keys are payload fields, never SQL. */
export const SORT_KEYS = Object.freeze(['org', 'status', 'tierAsOf', 'referredContacts', 'revenueCents', 'lastReferral']);

/**
 * Sort the per-org rows.
 *
 * ⛔ Returns a NEW array. Sorting the prop in place mutates the payload React
 * is still holding, so a re-render would show a different order from the one
 * the data arrived in, with nothing to explain it.
 */
export function sortRows(rows, key, dir = 'desc') {
  const k = SORT_KEYS.includes(key) ? key : 'revenueCents';
  const sign = dir === 'asc' ? 1 : -1;
  /*
   * ⛔⛔ TERMINATED PARTNERS SINK TO THE BOTTOM, WHATEVER THE SORT — Card
   * P220 add-on (Daniel terminated the demo partner, 1 Nov).
   *
   * ⚠ They are NOT filtered out. A terminated partner's referrals and
   * revenue are real history and still count in the money; removing the row
   * would make the table stop adding up to the tiles above it. Sinking and
   * greying them says "this partner is over" without deleting what they did.
   *
   * ⚠ Applied BEFORE the chosen key, so a sort by revenue cannot lift a
   * terminated org back into the top of the list.
   */
  const live = (r) => (r.status === 'terminated' ? 1 : 0);
  return [...(rows || [])].sort((a, b) => {
    const t = live(a) - live(b);
    if (t !== 0) return t;
    const x = a[k], y = b[k];
    if (x == null && y == null) return 0;
    /* ⚠ A missing value sorts LAST in both directions — never as zero, which
       would place an org that has never referred above one that has. */
    if (x == null) return 1;
    if (y == null) return -1;
    if (typeof x === 'number' && typeof y === 'number') return (x - y) * sign;
    return String(x).localeCompare(String(y)) * sign;
  });
}

/**
 * The CSV for the per-org table.
 *
 * ⛔⛔ THE HEADER LIST IS THE WHOLE CONTRACT. A commission column cannot
 * appear here unless someone adds it to this array, which is exactly what the
 * red arm watches. Revenue is written in DOLLARS because a reader opening this
 * in Excel beside the dashboard is comparing it with dollars on screen.
 */
export const CSV_HEADERS = Object.freeze(
  ['Partner', 'Status', 'Tier (as of 2026-10-03)', 'Referred contacts', 'Revenue (USD)', 'Last referral']);

/**
 * One cell of UNTRUSTED TEXT — an org name, a status, a tier.
 *
 * ⚠ A leading =, +, - or @ is executed by Excel as a formula, so it is
 * prefixed with an apostrophe.
 *
 * ⛔⛔ TEXT ONLY, AND THAT DISTINCTION IS THE POINT. A test caught this
 * guard turning the revenue `-8.00` into the TEXT `'-8.00`, because a
 * negative number legitimately begins with a minus. Excel then cannot sum
 * the column — a refund silently stops counting. Numbers this component
 * formats itself cannot be a formula and go out through `csvNumber`.
 */
const csvCell = (v) => {
  const s = v == null ? '' : String(v);
  const safe = /^[=+\-@]/.test(s) ? `'${s}` : s;
  return /[",\n]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
};

/** A figure WE produced: never quoted, never apostrophe-prefixed, may be negative. */
const csvNumber = (v) => (v == null ? '' : String(v));

export function affiliateCsv(rows) {
  const lines = [CSV_HEADERS.map(csvCell).join(',')];
  for (const r of rows || []) {
    lines.push([
      csvCell(r.org), csvCell(r.status), csvCell(r.tierAsOf),
      csvNumber(r.referredContacts),
      /* ⚠ Blank, not 0.00 — a spreadsheet column of zeroes is a claim. */
      csvNumber(r.revenueCents == null ? null : (Number(r.revenueCents) / 100).toFixed(2)),
      csvCell(r.lastReferral ? String(r.lastReferral).slice(0, 10) : ''),
    ].join(','));
  }
  return lines.join('\n');
}

/** Chart rows, in the order the payload ranked them. */
export function chartData(chart) {
  if (!chart) return [];
  const out = (chart.top || []).map((t) => ({ org: t.org, cents: t.revenueCents, kind: 'top' }));
  if (chart.others) out.push({ org: chart.others.org, cents: chart.others.revenueCents, kind: 'others' });
  /* ⚠ Net-negative orgs are drawn LAST and in their own colour. They are not
     folded into "Others": a negative wedge cancels real revenue beside it. */
  if (chart.netNegative) {
    out.push({ org: `Refunded (${chart.netNegative.count})`, cents: chart.netNegative.revenueCents, kind: 'negative' });
  }
  return out;
}

const COLOURS = { top: 'var(--zg-accent, #3b7ddd)', others: 'var(--zg-muted, #9aa5b1)', negative: 'var(--zg-danger, #c0392b)' };

export default function AffiliatePanel({ data, state, reason, C = {}, shareOfCollected = null }) {
  const [sortKey, setSortKey] = React.useState('revenueCents');
  const [sortDir, setSortDir] = React.useState('desc');

  /*
   * ⛔ A FAILED LOAD IS NOT AN EMPTY PROGRAMME. Rendering zeros when the query
   * failed tells Carlos the affiliate channel produced nothing, which is a
   * different and much worse claim than "this did not load".
   */
  if (state === 'failed') {
    return (
      <section data-testid="affiliate-panel" aria-label="Affiliate programme">
        <h3>Affiliate programme</h3>
        <p data-testid="affiliate-failed" role="status">
          Not loaded{reason ? ` — ${reason}` : ''}. These figures are unavailable; they are not zero.
        </p>
      </section>
    );
  }
  if (!data) {
    return (
      <section data-testid="affiliate-panel" aria-label="Affiliate programme">
        <h3>Affiliate programme</h3>
        <p data-testid="affiliate-loading" role="status">Loading…</p>
      </section>
    );
  }

  const t = data.tiles || {};
  const rows = sortRows(data.table, sortKey, sortDir);
  const bars = chartData(data.chart);

  const onSort = (k) => {
    if (k === sortKey) { setSortDir(sortDir === 'asc' ? 'desc' : 'asc'); return; }
    setSortKey(k); setSortDir(k === 'org' || k === 'status' ? 'asc' : 'desc');
  };

  return (
    <section data-testid="affiliate-panel" aria-label="Affiliate programme">
      <h3>Affiliate programme</h3>
      <div data-testid="affiliate-range" style={{ fontSize: 11, color: C.muted }}>
        {data.range ? data.range.label : ''}
      </div>

      {/*
        * ⭐ THE SHARE OF TOTAL COLLECTED — Card P220 Part A.
        *
        * ⛔ Passed IN, not computed here. The denominator is the finance
        * envelope's Collected for the same window; this panel's own channel
        * does not have it, and querying it separately would be a second
        * answer to "what percentage". Absent when the caller cannot supply
        * it — never a 0% that would read as "the channel earned nothing".
        */}
      {shareOfCollected ? (
        <div data-testid="affiliate-share" style={{ fontSize: 11, color: C.muted }}>
          {shareOfCollected}
        </div>
      ) : null}

      {/* ⛔ The ONLY commission text anywhere in this panel. */}
      {data.commissions && data.commissions.shown === false ? (
        <p data-testid="affiliate-commissions-paused"
           style={{ fontSize: 11, color: C.faint || '#8a94a0', fontStyle: 'italic' }}>
          {data.commissions.note}
        </p>
      ) : null}

      <div data-testid="affiliate-tiles"
           style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 }}>
        <Tile label="Active partners" value={t.activePartners}
              sub={`${t.totalPartners} total`} testid="affiliate-tile-partners" C={C} />
        <Tile label="Referred leads" value={t.referredLeads}
              sub={data.range ? data.range.label : ''} testid="affiliate-tile-leads" C={C} />
        <Tile label="Open opportunities" value={t.openOpps}
              /* ⚠ A STOCK, not a flow — it ignores the period, and says so. */
              sub="as of now" testid="affiliate-tile-opps" C={C} />
        {/*
          * ⛔⛔ "Not available" IS NOT "$0.00". When the GHL mirror has never
          * run, or has never finished a run, the server sends revenueTracked
          * false and a null figure. Rendering formatCents(null) would print
          * $0.00 and quietly turn an outage into a claim that the affiliate
          * channel earned nothing.
          */}
        <Tile label="Affiliate revenue"
              value={data.revenueTracked ? formatCents(t.revenueCents) : 'Not available'}
              sub={data.revenueTracked
                ? (data.revenueIsUpperBound ? 'upper bound' : '')
                : (data.revenueUntrackedMessage || 'the payment mirror has not run')}
              testid="affiliate-tile-revenue" C={C} />
      </div>

      {bars.length ? (
        <div data-testid="affiliate-chart" style={{ width: '100%', height: 220, marginTop: 16 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={bars} layout="vertical" margin={{ left: 8, right: 16, top: 8, bottom: 8 }}>
              <XAxis type="number" tickFormatter={(v) => formatCents(v)} />
              <YAxis type="category" dataKey="org" width={130} />
              <Tooltip formatter={(v) => formatCents(v)} />
              <Bar dataKey="cents" isAnimationActive={false}>
                {bars.map((b) => <Cell key={b.org} fill={COLOURS[b.kind]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      ) : null}

      <div style={{ marginTop: 12, overflowX: 'auto' }}>
        <table data-testid="affiliate-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
          <thead>
            <tr>
              <Th k="org" label="Partner" {...{ sortKey, sortDir, onSort }} />
              <Th k="status" label="Status" {...{ sortKey, sortDir, onSort }} />
              {/* ⚠ The date is IN the header. It is never just "Tier". */}
              <Th k="tierAsOf" label={`Tier (as of ${data.tierAsOf})`} {...{ sortKey, sortDir, onSort }} />
              <Th k="referredContacts" label="Referred" {...{ sortKey, sortDir, onSort }} />
              <Th k="revenueCents" label="Revenue" {...{ sortKey, sortDir, onSort }} />
              <Th k="lastReferral" label="Last referral" {...{ sortKey, sortDir, onSort }} />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const dead = r.status === 'terminated';
              return (
              <tr key={r.orgId} data-testid={`affiliate-row-${r.orgId}`}
                  data-terminated={dead ? 'true' : undefined}
                  /* ⚠ Greyed, not hidden — and opacity alone would be a
                     colour-only signal, so the badge below carries the word. */
                  style={dead ? { opacity: 0.6, color: C.muted } : undefined}>
                <td>
                  {r.org}
                  {dead ? (
                    <span data-testid={`affiliate-terminated-${r.orgId}`}
                          style={{ marginLeft: 6, fontSize: 10, fontWeight: 700,
                                   padding: '1px 5px', borderRadius: 999,
                                   border: `1px solid ${C.border}`, color: C.muted }}>
                      terminated
                    </span>
                  ) : null}
                </td>
                <td>{r.status}</td>
                <td>{r.tierAsOf}</td>
                <td style={{ textAlign: 'right' }}>{r.referredContacts}</td>
                <td style={{ textAlign: 'right' }}>
                  {r.revenueCents == null ? '—' : formatCents(r.revenueCents)}
                </td>
                <td>{r.lastReferral ? String(r.lastReferral).slice(0, 10) : '—'}</td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <button type="button" data-testid="affiliate-export-csv"
              onClick={() => saveCsv(affiliateCsv(rows), 'affiliate-partners.csv')}>
        Export CSV
      </button>
    </section>
  );
}

function Tile({ label, value, sub, testid, C }) {
  return (
    <div data-testid={testid}>
      <div style={{ fontSize: 11, fontWeight: 600, color: C.muted }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: C.text }}>{value}</div>
      {sub ? <div style={{ fontSize: 11, color: C.faint }}>{sub}</div> : null}
    </div>
  );
}

function Th({ k, label, sortKey, sortDir, onSort }) {
  const active = sortKey === k;
  return (
    <th scope="col" style={{ textAlign: 'left', padding: '4px 6px', borderBottom: '1px solid rgba(128,128,128,.3)' }}
        aria-sort={active ? (sortDir === 'asc' ? 'ascending' : 'descending') : 'none'}>
      <button type="button" data-testid={`affiliate-sort-${k}`} onClick={() => onSort(k)}
              style={{ background: 'none', border: 'none', padding: 0, font: 'inherit', cursor: 'pointer', fontWeight: 600 }}>
        {label}{active ? (sortDir === 'asc' ? ' ▲' : ' ▼') : ''}
      </button>
    </th>
  );
}
